import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import {
  updateSetting,
  getSetting,
  getCacheWarmStatus,
  refreshCache,
  PERFORMERS_MIN_SPEND_KEY,
  PERFORMERS_MIN_SPEND_DEFAULT,
} from '../api/settings';
import {
  getAccounts,
  createAccount,
  updateAccount,
  deleteAccount,
  testAccountConnection,
  AdAccount,
} from '../api/accounts';

export default function Settings() {
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  // Dashboard-cache warm-up freshness — daily 07:00 IL cron populates the
  // persistent cache; users see this timestamp on the Dashboard header.
  const { data: cacheWarmStatus } = useQuery({
    queryKey: ['cache-warm-status'],
    queryFn: getCacheWarmStatus,
    refetchInterval: 30000,
  });

  const refreshCacheMutation = useMutation({
    mutationFn: refreshCache,
    onSuccess: () => {
      // Purge the dashboard/insights query caches so the pages re-fetch
      // once the warm-up job finishes populating the persistent cache.
      queryClient.invalidateQueries({ queryKey: ['insights-summary'] });
      queryClient.invalidateQueries({ queryKey: ['insights'] });
      queryClient.invalidateQueries({ queryKey: ['website-stats'] });
      queryClient.invalidateQueries({ queryKey: ['dm-stats'] });
      queryClient.invalidateQueries({ queryKey: ['cache-warm-status'] });
    },
  });

  // Performers min-spend threshold (config). Local draft mirrored from server.
  const { data: performersMinSpendValue } = useQuery({
    queryKey: ['setting', PERFORMERS_MIN_SPEND_KEY],
    queryFn: () => getSetting<number>(PERFORMERS_MIN_SPEND_KEY),
    staleTime: 1000 * 60 * 5,
  });
  const [performersMinSpendDraft, setPerformersMinSpendDraft] = useState<string>('');
  const effectivePerformersMinSpend =
    typeof performersMinSpendValue === 'number'
      ? performersMinSpendValue
      : PERFORMERS_MIN_SPEND_DEFAULT;

  // State for adding new accounts
  const [showAddAccount, setShowAddAccount] = useState<'facebook' | 'tiktok' | null>(null);
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountId, setNewAccountId] = useState('');
  const [newAccessToken, setNewAccessToken] = useState('');
  const [accountError, setAccountError] = useState<string | null>(null);

  // State for editing accounts
  const [editingAccount, setEditingAccount] = useState<AdAccount | null>(null);
  const [editAccountName, setEditAccountName] = useState('');
  const [editAccessToken, setEditAccessToken] = useState('');
  const [editError, setEditError] = useState<string | null>(null);

  // Accounts query
  const { data: accounts, isLoading: accountsLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: getAccounts,
  });

  // Initialize state from fetched data
  // Update setting mutation
  const updateSettingMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }) => {
      await updateSetting(key, value);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sync-scheduler-status'] });
    },
  });

  // Create account mutation
  const createAccountMutation = useMutation({
    mutationFn: createAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setShowAddAccount(null);
      setNewAccountName('');
      setNewAccountId('');
      setNewAccessToken('');
      setAccountError(null);
    },
    onError: (error: Error) => {
      setAccountError(error.message || 'Failed to create account');
    },
  });

  // Delete account mutation
  const deleteAccountMutation = useMutation({
    mutationFn: deleteAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });

  // Update account mutation
  const updateAccountMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; accessToken?: string } }) =>
      updateAccount(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setEditingAccount(null);
      setEditAccountName('');
      setEditAccessToken('');
      setEditError(null);
    },
    onError: (error: Error) => {
      setEditError(error.message || 'Failed to update account');
    },
  });

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: testAccountConnection,
  });

  // Sync draft with the server value whenever it (re-)loads.
  useEffect(() => {
    if (typeof performersMinSpendValue === 'number') {
      setPerformersMinSpendDraft(String(performersMinSpendValue));
    }
  }, [performersMinSpendValue]);

  const handlePerformersMinSpendCommit = async () => {
    const parsed = parseFloat(performersMinSpendDraft);
    if (Number.isNaN(parsed) || parsed < 0) {
      // Revert to last known good value
      setPerformersMinSpendDraft(String(effectivePerformersMinSpend));
      return;
    }
    if (parsed === effectivePerformersMinSpend) return;
    await updateSettingMutation.mutateAsync({
      key: PERFORMERS_MIN_SPEND_KEY,
      value: parsed,
    });
    queryClient.invalidateQueries({ queryKey: ['setting', PERFORMERS_MIN_SPEND_KEY] });
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  const handleAddAccount = async () => {
    if (!showAddAccount || !newAccountName || !newAccountId || !newAccessToken) {
      setAccountError('Please fill in all fields');
      return;
    }

    setAccountError(null);
    await createAccountMutation.mutateAsync({
      platform: showAddAccount,
      name: newAccountName,
      platformAccountId: showAddAccount === 'facebook' ? `act_${newAccountId.replace('act_', '')}` : newAccountId,
      accessToken: newAccessToken,
    });
  };

  const handleStartEdit = (account: AdAccount) => {
    setEditingAccount(account);
    setEditAccountName(account.name);
    setEditAccessToken('');
    setEditError(null);
  };

  const handleUpdateAccount = async () => {
    if (!editingAccount) return;

    if (!editAccountName) {
      setEditError('Account name is required');
      return;
    }

    setEditError(null);
    const updateData: { name?: string; accessToken?: string } = {};

    if (editAccountName !== editingAccount.name) {
      updateData.name = editAccountName;
    }

    if (editAccessToken) {
      updateData.accessToken = editAccessToken;
    }

    if (Object.keys(updateData).length === 0) {
      setEditError('No changes to save');
      return;
    }

    await updateAccountMutation.mutateAsync({
      id: editingAccount._id,
      data: updateData,
    });
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'facebook':
        return (
          <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
        );
      case 'tiktok':
        return (
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>
      </div>

      {/* Settings Sections */}
      <div className="space-y-6">
        {/* Profile Section */}
        <div className="rounded-lg border bg-white p-6 shadow-sm">
          <h2 className="text-lg font-medium text-gray-900">{t('settings.profile')}</h2>

          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('auth.email')}
              </label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="mt-1 block w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-gray-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Name
              </label>
              <input
                type="text"
                defaultValue={user?.name || ''}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <button className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90">
              {t('common.save')}
            </button>
          </div>
        </div>

        {/* Language Section */}
        <div className="rounded-lg border bg-white p-6 shadow-sm">
          <h2 className="text-lg font-medium text-gray-900">{t('settings.language')}</h2>

          <div className="mt-4">
            <div className="flex gap-4">
              <button
                onClick={() => handleLanguageChange('en')}
                className={`flex-1 rounded-md border px-4 py-3 text-sm font-medium ${
                  i18n.language === 'en'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                English
              </button>
              <button
                onClick={() => handleLanguageChange('he')}
                className={`flex-1 rounded-md border px-4 py-3 text-sm font-medium ${
                  i18n.language === 'he'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Hebrew
              </button>
            </div>
          </div>
        </div>

        {/* Dashboard Cache Section */}
        <div className="rounded-lg border bg-white p-6 shadow-sm">
          <h2 className="text-lg font-medium text-gray-900">
            {t('cache.title', 'Dashboard data refresh')}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t(
              'cache.subtitle',
              'Dashboard, Insights, and account KPIs are fetched from Facebook/TikTok/Zoho once per day at 07:00 and stored. Every view loads instantly from the stored copy. Use "Refresh now" if you need up-to-the-minute numbers before the next scheduled refresh.'
            )}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <button
              onClick={() => refreshCacheMutation.mutate()}
              disabled={refreshCacheMutation.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
            >
              {refreshCacheMutation.isPending
                ? t('cache.refreshing', 'Refreshing…')
                : t('cache.refreshNow', 'Refresh now')}
            </button>
            <div className="text-sm text-gray-600">
              <div>
                <span className="text-gray-500">
                  {t('cache.lastRefreshed', 'Last refreshed:')}
                </span>{' '}
                <span className="font-medium text-gray-900">
                  {formatDate(cacheWarmStatus?.lastWarmedAt ?? null)}
                </span>
              </div>
              <div>
                <span className="text-gray-500">
                  {t('cache.storedEntries', 'Stored entries:')}
                </span>{' '}
                <span className="font-medium text-gray-900">
                  {cacheWarmStatus?.entryCount ?? '—'}
                </span>
              </div>
            </div>
            {refreshCacheMutation.isSuccess && (
              <span className="text-sm text-green-600">
                {t(
                  'cache.refreshQueued',
                  'Refresh started. Numbers will update in 2–4 minutes.'
                )}
              </span>
            )}
            {refreshCacheMutation.isError && (
              <span className="text-sm text-red-600">
                {t('cache.refreshFailed', 'Refresh failed')}
              </span>
            )}
          </div>
        </div>

        {/* Performers Section */}
        <div className="rounded-lg border bg-white p-6 shadow-sm">
          <h2 className="text-lg font-medium text-gray-900">Top / Underperformers</h2>
          <p className="mt-1 text-sm text-gray-500">
            Threshold for the Top Performers and Underperformers widgets on the dashboard.
            Ads with less spend than this value over the selected date range are ignored.
          </p>
          <div className="mt-4 max-w-sm">
            <label className="block text-sm font-medium text-gray-700">
              Minimum spend (₪)
            </label>
            <div className="mt-1 flex items-center gap-3">
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="100"
                value={performersMinSpendDraft}
                onChange={(e) => setPerformersMinSpendDraft(e.target.value)}
                onBlur={handlePerformersMinSpendCommit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur();
                  }
                }}
                disabled={updateSettingMutation.isPending}
                className="block w-40 rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-100"
              />
              <span className="text-xs text-gray-500">
                Default: ₪{PERFORMERS_MIN_SPEND_DEFAULT.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* Ad Accounts Section */}
        <div className="rounded-lg border bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium text-gray-900">Ad Accounts</h2>
              <p className="mt-1 text-sm text-gray-500">
                Connect your advertising accounts to track performance metrics.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowAddAccount('facebook')}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <svg className="h-4 w-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Add Facebook
              </button>
              <button
                onClick={() => setShowAddAccount('tiktok')}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                </svg>
                Add TikTok
              </button>
            </div>
          </div>

          {/* Add Account Modal */}
          {showAddAccount && (
            <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center gap-2 mb-4">
                {getPlatformIcon(showAddAccount)}
                <h3 className="text-sm font-medium text-gray-900">
                  Add {showAddAccount === 'facebook' ? 'Facebook' : 'TikTok'} Account
                </h3>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Account Name
                  </label>
                  <input
                    type="text"
                    value={newAccountName}
                    onChange={(e) => setNewAccountName(e.target.value)}
                    placeholder={showAddAccount === 'facebook' ? 'My Facebook Ads' : 'My TikTok Ads'}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {showAddAccount === 'facebook' ? 'Ad Account ID' : 'Advertiser ID'}
                  </label>
                  <input
                    type="text"
                    value={newAccountId}
                    onChange={(e) => setNewAccountId(e.target.value)}
                    placeholder={showAddAccount === 'facebook' ? 'act_123456789' : '7508765801379610642'}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {showAddAccount === 'facebook'
                      ? 'Find this in Facebook Business Manager under Ad Accounts'
                      : 'Find this in TikTok Ads Manager under Account Info'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Access Token
                  </label>
                  <input
                    type="password"
                    value={newAccessToken}
                    onChange={(e) => setNewAccessToken(e.target.value)}
                    placeholder="Paste your access token here"
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {showAddAccount === 'facebook'
                      ? 'Generate a token from Meta Business Suite or Graph API Explorer'
                      : 'Generate from TikTok Business API portal after app approval'}
                  </p>
                </div>

                {accountError && (
                  <p className="text-sm text-red-600">{accountError}</p>
                )}

                <div className="flex gap-2 pt-2">
                  <button
                    onClick={handleAddAccount}
                    disabled={createAccountMutation.isPending}
                    className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
                  >
                    {createAccountMutation.isPending ? 'Adding...' : 'Add Account'}
                  </button>
                  <button
                    onClick={() => {
                      setShowAddAccount(null);
                      setNewAccountName('');
                      setNewAccountId('');
                      setNewAccessToken('');
                      setAccountError(null);
                    }}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Account Modal */}
          {editingAccount && (
            <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-center gap-2 mb-4">
                {getPlatformIcon(editingAccount.platform)}
                <h3 className="text-sm font-medium text-gray-900">
                  Edit {editingAccount.name}
                </h3>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Account Name
                  </label>
                  <input
                    type="text"
                    value={editAccountName}
                    onChange={(e) => setEditAccountName(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    New Access Token (leave empty to keep current)
                  </label>
                  <input
                    type="password"
                    value={editAccessToken}
                    onChange={(e) => setEditAccessToken(e.target.value)}
                    placeholder="Paste new access token here"
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Only fill this if you want to update the access token
                  </p>
                </div>

                {editError && (
                  <p className="text-sm text-red-600">{editError}</p>
                )}

                <div className="flex gap-2 pt-2">
                  <button
                    onClick={handleUpdateAccount}
                    disabled={updateAccountMutation.isPending}
                    className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
                  >
                    {updateAccountMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={() => {
                      setEditingAccount(null);
                      setEditAccountName('');
                      setEditAccessToken('');
                      setEditError(null);
                    }}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Connected Accounts List */}
          <div className="mt-4">
            {accountsLoading ? (
              <div className="animate-pulse space-y-3">
                <div className="h-16 rounded-md bg-gray-200"></div>
                <div className="h-16 rounded-md bg-gray-200"></div>
              </div>
            ) : accounts && accounts.length > 0 ? (
              <div className="space-y-3">
                {accounts.map((account) => (
                  <div
                    key={account._id}
                    className="flex items-center justify-between rounded-md border border-gray-200 bg-white p-4"
                  >
                    <div className="flex items-center gap-3">
                      {getPlatformIcon(account.platform)}
                      <div>
                        <p className="font-medium text-gray-900">{account.name}</p>
                        <p className="text-sm text-gray-500">
                          {account.platform === 'facebook' ? 'Facebook' : 'TikTok'} · {account.platformAccountId}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          account.syncStatus === 'syncing'
                            ? 'bg-blue-100 text-blue-800'
                            : account.syncStatus === 'error'
                            ? 'bg-red-100 text-red-800'
                            : account.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {account.syncStatus === 'syncing'
                          ? 'Syncing'
                          : account.syncStatus === 'error'
                          ? 'Error'
                          : account.isActive
                          ? 'Active'
                          : 'Inactive'}
                      </span>
                      <button
                        onClick={() => handleStartEdit(account)}
                        className="text-sm text-gray-600 hover:text-gray-800"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => testConnectionMutation.mutate(account._id)}
                        disabled={testConnectionMutation.isPending}
                        className="text-sm text-primary hover:text-primary/80"
                      >
                        Test
                      </button>
                      <button
                        onClick={() => {
                          if (confirm('Are you sure you want to remove this account?')) {
                            deleteAccountMutation.mutate(account._id);
                          }
                        }}
                        disabled={deleteAccountMutation.isPending}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-gray-300 p-6 text-center">
                <p className="text-sm text-gray-500">
                  No ad accounts connected yet. Click "Add Facebook" or "Add TikTok" to get started.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Password Section */}
        <div className="rounded-lg border bg-white p-6 shadow-sm">
          <h2 className="text-lg font-medium text-gray-900">{t('settings.changePassword')}</h2>

          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('settings.currentPassword')}
              </label>
              <input
                type="password"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('settings.newPassword')}
              </label>
              <input
                type="password"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('settings.confirmPassword')}
              </label>
              <input
                type="password"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <button className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90">
              {t('settings.changePassword')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
